
export type CompanyType =
    | 'standard'
    | 'nopii'
    | 'data_broker'
    | 'advertising_marketing'
    | 'job_recruiting'
    | 'background_check'
    | 'data_enrichment'
    | 'healthcare_insurance'
    | 'geolocation'
    | 'surveillance_tech'
    | 'work_income_verification'
    | 'people_search'
    | 'people_search_affiliate'
    | 'social_media'
    | 'service_provider'
    | 'political'
    | 'non_profit'
    | 'industry_optout'
    | 'banking_finance'
    | 'action'
    | 'government'
    | 'sandbox'
    | 'affiliate'
    | 'non_public'
    | 'blur_location';

    export interface CompanyTypeLabel {
        label: string;
        value: string;
    }

export interface CompanyExposure {
    id: number;
    name: string;
    slug: string;
    count: number;
    logo: string;
    url: string;
    events: number;
    type: CompanyTypeLabel;
    last_action: string;
    status: ExposureStatus;
    active: number;
    in_progress: number;
    closed: number;
}

export interface CompanyExposureDetail {
    exposure_id: string;
    company_id: number;
    person_id: number;
    last_exposure: string;
    last_action: string;
    next_action: ExposureNextAction;
    remediation: ExposureRemediation;
    status: ExposureStatus;
    severity: ExposureSeverity;
    events: number;
}

export interface PersonExposure {
    id: string;
    company_id: number;
    person_id: number;
    priority: number;
    severity: ExposureSeverity;
    last_scan_at: string | null;
    last_seen_at: string | null;
    status: ExposureStatus;
    last_action_at: string;
    created_at: string;
}

export enum ScannerStatus {

}

export interface Exposure {
    id: string;
    company_id: number;
    company: {
        logo: string,
        name: string,
        type: {
            label: string,
            value: string,
        },
        slug: string,
        scanner: string,
    };
    person_id: number;
    source: string;
    priority: number;
    last_scan_at: string | null;
    last_seen_at: string | null;
    last_action_at: string | null;
    next_action: ExposureNextAction;
    next_action_at: string | null;
    status: ExposureStatus;
    created_at: string;
    closed_at: string | null;
    in_progress_at: string | null;
    remediation: ExposureRemediation;
    rapid_response?: {
        exposure_id: string;
        status: 'pending' | 'assigned' | 'submitted' | 'finished';
        charge_amount: number;
        escalation_notes?: string;
        assigned_to?: { id: number; first_name: string; last_name: string; };
        elapsed_time: string;
        created_at: string;
        assigned_at?: string;
        submitted_at?: string;
        finished_at?: string;
    } | null;
}

export interface AdditionalInfo {
    all: number;
    pending: number;
    in_progress: number;
    not_found: number;
    closed: number;
    failed: number;
    total_actions: number;
    recent_actions: number;
}

export interface PreparedData {
    name: string;
    type: CompanyType;
    events: number;
    logo: string;
    last_action: string;
    active: number;
    in_progress: number;
    pending: number;
    closed: number;
    not_found: number;
    status: ExposureStatus; // Changed from ExposureStatus to string for display labels
    url: string;

    exposures: Array<Exposure>;
}

export enum ExposureStatus {
    EXPOSED = 'exposed',
    IN_PROGRESS = 'in_progress',
    PENDING = 'pending',
    CLOSED = 'closed',
    NOT_FOUND = 'not_found',
    FAILED = 'failed'
}

export enum ExposureNextAction {
    MONITOR = 'monitor',
    CONFIRM = 'confirm',
    SUBMIT_PRIVACYBEE = 'submit_privacybee',
    SUBMIT_MANUAL = 'submit_manual'
}

export enum ExposureRemediation {
    MANUAL = 'manual',
    GRT = 'grt',
    SST = 'sst',
    RRT = 'rrt',
    EMAIL = 'email'
}

export enum ExposureSeverity {
    CRITICAL = 'critical',
    HIGH = 'high',
    MEDIUM = 'medium',
    LOW = 'low',
    INFO = 'info',
    SUPPRESSED = 'suppressed'
}

// Utility function to map ExposureSeverity to display labels
export function getExposureSeverityLabel(severity: ExposureSeverity): string {
    const severityMap: Record<ExposureSeverity, string> = {
        [ExposureSeverity.CRITICAL]: 'Critical',
        [ExposureSeverity.HIGH]: 'High',
        [ExposureSeverity.MEDIUM]: 'Medium',
        [ExposureSeverity.LOW]: 'Low',
        [ExposureSeverity.INFO]: 'Info',
        [ExposureSeverity.SUPPRESSED]: 'Suppressed'
    };
    return severityMap[severity] || 'Low';
}

// Utility function to map ExposureStatus to display labels
export function getExposureStatusLabel(status: ExposureStatus): string {
    const statusMap: Record<ExposureStatus, string> = {
        [ExposureStatus.EXPOSED]: 'Exposed',
        [ExposureStatus.IN_PROGRESS]: 'Exposed',
        [ExposureStatus.PENDING]: 'Exposed',
        [ExposureStatus.CLOSED]: 'Closed',
        [ExposureStatus.NOT_FOUND]: 'Clean',
        [ExposureStatus.FAILED]: 'Failed'
    };
    return statusMap[status];
}

// Utility function to map ExposureNextAction to display labels
export function getExposureNextActionLabel(action: ExposureNextAction): string {
    const actionMap: Record<ExposureNextAction, string> = {
        [ExposureNextAction.MONITOR]: 'Monitor',
        [ExposureNextAction.CONFIRM]: 'Confirm',
        [ExposureNextAction.SUBMIT_PRIVACYBEE]: 'Automated Submission',
        [ExposureNextAction.SUBMIT_MANUAL]: 'User Submission'
    };
    return actionMap[action];
}

// Utility function to map ExposureRemediation to display labels
export function getExposureRemediationLabel(remediation: ExposureRemediation): string {
    const remediationMap: Record<ExposureRemediation, string> = {
        [ExposureRemediation.MANUAL]: 'Manual',
        [ExposureRemediation.GRT]: 'Global Response Team',
        [ExposureRemediation.SST]: 'Signature Services Team',
        [ExposureRemediation.RRT]: 'Response Response Team',
        [ExposureRemediation.EMAIL]: 'SARA'
    };
    return remediationMap[remediation];
}

// Utility function to map CompanyType to display labels (matching PHP enum labels)
export function getCompanyTypeLabel(type: CompanyType): string {
    const typeMap: Record<CompanyType, string> = {
        'standard': 'Standard',
        'nopii': 'No PII',
        'data_broker': 'Data Broker - List Broker',
        'advertising_marketing': 'Data Broker - Marketing',
        'job_recruiting': 'Data Broker - Job Recruiting',
        'background_check': 'Data Broker - Background Check',
        'data_enrichment': 'Data Broker - Data Enrichment',
        'healthcare_insurance': 'Data Broker - Healthcare & Insurance',
        'geolocation': 'Data Broker - Geolocation',
        'surveillance_tech': 'Data Broker - Surveillance Tech',
        'work_income_verification': 'Data Broker - Work Income Verification',
        'people_search': 'Data Broker - People Search Site',
        'people_search_affiliate': 'Data Broker - People Search Site Affiliate',
        'social_media': 'Social Media',
        'service_provider': 'Service Provider',
        'political': 'Political',
        'non_profit': 'Non-Profit',
        'industry_optout': 'Industry Opt-Out',
        'banking_finance': 'Banking',
        'action': 'Custom Action',
        'government': 'Government',
        'sandbox': 'Sandbox',
        'affiliate': 'Affiliate',
        'non_public': 'Non-Public',
        'blur_location': 'Blur Location'
    };
    return typeMap[type];
}